// R11P GEOLOGICAL P0.6-NITA-02A — investor manual-payment evidence (Geo-API RC2).
//
// Contract (GIISDEC-GEO-API-P0.6-RC2):
//   1. POST /api/manual-payments/proof   multipart "file" (PDF/JPEG/PNG)
//        -> 201 { file: { id, originalName, contentType, byteSize, checksumSha256, uploadedAt } }
//   2. POST /api/manual-payments         { invoiceId, amountClaimed, proofFileId, ... }
//        -> 201 { submission, payment, reference }   status: pending_verification
// A submission is EVIDENCE of an already-made payment awaiting Finance
// verification — never "paid", "confirmed" or an online payment.
//
// Pure module (no imports) so the ordering and wording are unit-testable.

export const PROOF_ACCEPT = 'application/pdf,image/jpeg,image/png,.pdf,.jpg,.jpeg,.png';
export const PROOF_MAX_BYTES = 10 * 1024 * 1024;
const PROOF_TYPES: Record<string, string[]> = {
  'application/pdf': ['.pdf'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
};

export const PENDING_LABEL = 'Pending Finance verification';

export interface ProofFileLike { name: string; size: number; type: string }
export type ProofCheck = { ok: true } | { ok: false; message: string };

export function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n < 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Client-side usability check only — the API re-checks the real bytes and is
 * authoritative. Refuses obviously wrong files before any upload.
 */
export function checkProofFile(file: ProofFileLike | null | undefined): ProofCheck {
  if (!file) return { ok: false, message: 'Choose your payment proof (PDF, JPEG or PNG).' };
  const name = String(file.name || '');
  const ext = name.includes('.') ? name.slice(name.lastIndexOf('.')).toLowerCase() : '';
  const typeOk = Object.prototype.hasOwnProperty.call(PROOF_TYPES, file.type);
  const extOk = Object.values(PROOF_TYPES).some((exts) => exts.includes(ext));
  if (!typeOk || !extOk || (typeOk && ext && !PROOF_TYPES[file.type].includes(ext))) {
    return { ok: false, message: 'This file type is not accepted. Upload a PDF, JPEG or PNG file.' };
  }
  if (!(file.size > 0)) return { ok: false, message: 'This file is empty. Choose your payment proof again.' };
  if (file.size > PROOF_MAX_BYTES) {
    return { ok: false, message: `This file is ${formatBytes(file.size)}. The maximum size is ${formatBytes(PROOF_MAX_BYTES)}.` };
  }
  return { ok: true };
}

export interface ApiOutcome<T> { data: T | null; error: string | null; status: number }

/** Truthful wording for a failed proof upload. */
export function uploadFailureMessage(status: number, serverMessage?: string | null): string {
  switch (status) {
    case 400: return serverMessage || 'This file could not be accepted as payment proof. Upload a PDF, JPEG or PNG file.';
    case 401: return 'Your session has ended. Sign in again, then upload your payment proof.';
    case 403: return 'Payment evidence can only be submitted from an investor account with a completed investor profile.';
    case 413: return `This file is larger than the ${formatBytes(PROOF_MAX_BYTES)} limit. Upload a smaller file.`;
    case 0: return 'The upload could not reach GIISDEC. Check your connection and try again. Nothing has been submitted.';
    default: return 'Your payment proof could not be stored right now. Nothing has been submitted. Please try again later.';
  }
}

/** Truthful wording for a failed submission (after a successful upload). */
export function submitFailureMessage(status: number, serverMessage?: string | null): string {
  const m = String(serverMessage || '');
  if (status === 409) {
    if (/transaction reference/i.test(m)) return 'This transaction reference has already been submitted. Check the reference, or contact GIISDEC Finance if you believe this is wrong.';
    if (/already attached/i.test(m)) return 'This payment proof has already been used for another submission. Upload the proof for this payment.';
    return m || 'This payment evidence conflicts with an earlier submission.';
  }
  switch (status) {
    case 400: return m || 'Some details could not be accepted. Check the details and try again.';
    case 401: return 'Your session has ended. Sign in again and resubmit. Your evidence has not been submitted.';
    case 403: return m || 'This invoice cannot receive payment evidence from your account.';
    case 404: return /proof/i.test(m) ? 'Your uploaded proof could not be found. Upload it again.' : (m || 'The invoice could not be found.');
    case 0: return 'The submission could not reach GIISDEC. Your evidence has not been submitted. Try again.';
    default: return 'Your payment evidence could not be submitted right now. It has not been submitted. Please try again later.';
  }
}

export interface EvidenceDetails {
  invoiceId: string;
  amountClaimed: number;
  providerTxnRef?: string;
  payerName?: string;
  payerPhone?: string;
  paymentDatetime?: string;
  applicantNote?: string;
}

export interface UploadedProofLike { id: string; originalName: string; byteSize: number }

export type EvidenceResult<S> =
  | { stage: 'invalid'; message: string }
  | { stage: 'upload_failed'; message: string }
  | { stage: 'submit_failed'; message: string; proofFileId: string }
  | { stage: 'submitted'; submission: S; proofFileId: string };

/**
 * Upload first, then submit with the RETURNED file id. A failed or id-less
 * upload never reaches the submit call. When a previous attempt already stored
 * the proof (submit failed afterwards), its id can be reused instead of
 * uploading the same bytes again.
 */
export async function submitPaymentEvidence<S>(
  deps: {
    uploadProof: (file: File) => Promise<ApiOutcome<{ file: UploadedProofLike }>>;
    submit: (payload: EvidenceDetails & { proofFileId: string }) => Promise<ApiOutcome<{ submission: S }>>;
  },
  input: { details: EvidenceDetails; file: File | null; reuseProofFileId?: string | null },
): Promise<EvidenceResult<S>> {
  const d = input.details;
  if (!d.invoiceId) return { stage: 'invalid', message: 'Choose the invoice this payment was for.' };
  if (!(Number(d.amountClaimed) > 0)) return { stage: 'invalid', message: 'Enter the amount you paid.' };

  let proofFileId = input.reuseProofFileId || '';
  if (!proofFileId) {
    const check = checkProofFile(input.file);
    if ('message' in check) return { stage: 'invalid', message: check.message };
    const up = await deps.uploadProof(input.file as File);
    const id = up.data && up.data.file && up.data.file.id;
    if (up.error || !id) return { stage: 'upload_failed', message: uploadFailureMessage(up.status, up.error) };
    proofFileId = id;
  }

  const res = await deps.submit({ ...d, proofFileId });
  if (res.error || !res.data || !res.data.submission) {
    return { stage: 'submit_failed', message: submitFailureMessage(res.status, res.error), proofFileId };
  }
  return { stage: 'submitted', submission: res.data.submission, proofFileId };
}

/** Investor-facing label for a submission state (never "paid"/"successful" before Finance confirms). */
export function submissionStatusLabel(status: string): string {
  switch (status) {
    case 'pending_verification': return PENDING_LABEL;
    case 'confirmed': return 'Verified by GIISDEC Finance';
    case 'rejected': return 'Not accepted by GIISDEC Finance — correction needed';
    default: return 'Status unavailable';
  }
}
